// Internal modules
import { config } from './config';
import { $logger } from '../logger';
import { getCheckoutSessionFlags, getGlobals, getLocale } from '../globals';
import {
  getCountryConfigValue,
  getFactors,
  getInternalOverrides,
  hasExactTreatment,
  hasTreatment,
} from './utils';
import {
  AML_COUNTRIES,
  DCC_INELIGIBLE_COUNTRIES,
  DEFAULT_ENABLED_LANGUAGES,
} from '../constants';

const { isInternalRequest } = getGlobals();

const resolvers = {
  allowChangePhoneType: () => hasTreatment('FEATURE_ALLOW_CHANGE_PHONE_TYPE'),

  allowedBankTypes: () => {
    const { checkoutSession } = getGlobals().initialGqlData;
    const { cardProductClasses } = checkoutSession || {};
    const isDebitFlow = cardProductClasses?.includes('DEBIT') || false;

    // This logic is currently only used for MX
    // return featured bank list only if we are sure that it's not a debit flow
    if (!cardProductClasses || isDebitFlow) {
      return [];
    }

    return getCountryConfigValue('allowedBankTypes', config);
  },

  billingType: () => {
    const { checkoutSession } = getGlobals().initialGqlData;
    const { cart } = checkoutSession || {};
    const { billingType } = cart || null;

    return billingType;
  },

  blockNonDomesticPayer: () => getCheckoutSessionFlags().blockNonDomesticPayer,

  blockNonDomesticShipping: () =>
    getCheckoutSessionFlags().blockNonDomesticShipping,

  canDialingCodeChange: () => hasTreatment('FEATURE_ALLOW_CHANGE_DIALING_CODE'),

  enabledLanguages: () => {
    const { localeMetadata } = getGlobals().initialGqlData;
    const { supportedLanguages } = localeMetadata || {};

    return supportedLanguages || DEFAULT_ENABLED_LANGUAGES;
  },

  footerLinks: () => config.footerLinks,

  hasAddBank: () => hasTreatment('FEATURE_HAS_ADD_BANK'),

  hasAddressAutocomplete: () => {
    // @TO_DO @TECH_DEBT we disable GAAC because we use ANS by zip code in BR
    // we should remove this one once we ramp up GAAC in BR
    if (getLocale().country === 'BR') {
      return false;
    }
    return isInternalRequest || hasTreatment('FEATURE_ADDRESS_AUTOCOMPLETE');
  },

  hasAddressAutocompleteDisclaimer: () => {
    return hasExactTreatment({
      feature: 'FEATURE_ADDRESS_AUTOCOMPLETE',
      treatment: 'Trmt_FEATURE_ADDRESS_AUTOCOMPLETE_WITH_DISCLAIMER',
    });
  },

  hasAddressNormalization: () =>
    hasTreatment('FEATURE_HAS_ADDRESS_NORMALIZATION'),

  hasCardTypeSelector: () => hasTreatment('FEATURE_HAS_CARD_TYPE_SELECTOR'),

  hasConsumerAddBank: () => hasTreatment('FEATURE_HAS_CONSUMER_ADD_BANK'),

  hasDccDomesticRestriction: () =>
    hasTreatment('FEATURE_HAS_DCC_DOMESTIC_RESTRICTION'),

  hasDccIofDisclosure: () => hasTreatment('FEATURE_HAS_DCC_IOF_DISCLOSURE'),

  hasFooterEntityMessage: () =>
    hasTreatment('FEATURE_HAS_FOOTER_ENTITY_MESSAGE'),

  hasFooterPrivacyAndSecurityMessage: () =>
    hasTreatment('FEATURE_HAS_FOOTER_PRIVACY_AND_SECURITY_MSG'),

  hasFreeReturnShipping: () => {
    const { isFrsApplicable, isGuestEligible } = getCheckoutSessionFlags();

    return (
      isFrsApplicable &&
      !isGuestEligible &&
      hasTreatment('FEATURE_HAS_FREE_RETURN_SHIPPING')
    );
  },

  hasGuestTermsAndConditionsCheckbox: () =>
    hasTreatment('FEATURE_HAS_GUEST_TERMS_CHECKBOX'),

  hasMarketingOptInCheckbox: () =>
    hasTreatment('FEATURE_HAS_MARKETING_OPT_IN_CHECKBOX'),

  hasMerchantLogo: () => hasTreatment('FEATURE_MERCHANT_LOGO'),

  hasPasswordInfoSecRule: () =>
    hasTreatment('FEATURE_HAS_PASSWORD_INFO_SEC_RULE'),

  hasReturnAfterSubmitMessage: () =>
    hasTreatment('FEATURE_HAS_RETURN_AFTER_SUBMIT_MESSAGE'),

  hasSepaMandate: () => hasTreatment('FEATURE_HAS_SEPA_MANDATE'),

  hasSignupTermsAndConditionsCheckbox: () =>
    hasTreatment('FEATURE_HAS_SIGNUP_TERMS_CHECKBOX'),

  isAccountExistsErrorTrmt: () =>
    hasTreatment('FEATURE_ACCOUNT_EXISTS_ERROR_MESSAGE'),

  isAddressless: () => {
    const { checkoutSession = {} } = getGlobals().initialGqlData;
    const { merchant: { country: merchantCountry } = {} } = checkoutSession;

    const isAddresslessEligible =
      !getCheckoutSessionFlags().isShippingAddressRequired &&
      hasTreatment('FEATURE_IS_ADDRESSLESS');
    const isCrossBorder = merchantCountry !== getLocale().country;
    return isAddresslessEligible && !isCrossBorder;
  },

  isAllowChangeShipping: () =>
    getCheckoutSessionFlags().isChangeShippingAddressAllowed,

  isAmlCountry: () => AML_COUNTRIES.includes(getLocale().country),

  isCardTabDefault: () =>
    hasTreatment('FEATURE_DEFAULT_CARD_TAB_WHEN_BANK_ENABLED'),

  isDccEligible: () => !DCC_INELIGIBLE_COUNTRIES.includes(getLocale().country),

  isDonation: () => getCheckoutSessionFlags().isDonation,

  isDonationOptionallyShareAddress: () =>
    getCheckoutSessionFlags().isOptionallyShareAddress &&
    hasTreatment('FEATURE_DONATION_OPTIONALLY_SHARE_ADDRESS'),

  isForcedSignUp: () =>
    !getGlobals().initialGqlData.checkoutSession.isGuestEligible,

  isGuestEligible: () =>
    getCheckoutSessionFlags().isGuestEligible &&
    hasTreatment('FEATURE_IS_GUEST_ELIGIBLE'),

  isInstallmentsEligible: () => {
    const { checkoutSession } = getGlobals().initialGqlData;
    const {
      cart: { intent },
    } = checkoutSession;
    // Installments are eligible only for intent: SALE,
    // other intents like CAPTURE/AUTHORIZE/ORDER not eligible.
    return (
      intent === 'SALE' &&
      getCountryConfigValue('isInstallmentsEligible', config)
    );
  },

  isMarketingOptInCheckedByDefault: () =>
    hasTreatment('FEATURE_IS_MKT_OPT_IN_CHECKED_BY_DEFAULT'),

  isPOMAEligible: () =>
    getCheckoutSessionFlags().isGuestEligible &&
    hasTreatment('FEATURE_IS_POMA_ELIGIBLE'),

  isSntSupported: () => getCheckoutSessionFlags().isSntSupported,

  kycFields: () => getCountryConfigValue('kycFields', config),

  kycIdTypes: () => getCountryConfigValue('kycIdTypes', config),

  secondaryKycIdTypes: () =>
    getCountryConfigValue('secondaryKycIdTypes', config),

  shouldBlockDomesticTransactions: () =>
    hasTreatment('FEATURE_SHOULD_BLOCK_DOMESTIC_TRANSACTION'),

  shouldCollectCardProductClass: () =>
    hasTreatment('FEATURE_SHOULD_SHOW_CARD_PRODUCT_CLASS'),

  shouldDetectCupCardType: () =>
    hasTreatment('FEATURE_SHOULD_DETECT_CUP_CARDTYPE'),

  shouldDisableSignup: () => hasTreatment('FEATURE_SHOULD_DISABLE_SIGNUP'),

  shouldHandleAddressValidationError: () =>
    hasTreatment('FEATURE_SHOULD_HANDLE_ADDR_VALIDATION_ERR'),

  shouldHandleCountryChange: () =>
    hasTreatment('FEATURE_SHOULD_HANDLE_CNTRY_CHANGE'),

  shouldMaskCvv: () => hasTreatment('FEATURE_SHOULD_MASK_CVV'),

  shouldShowBNPL: () => {
    /*
     * Traffic distribution:
     * - Upstream presentment distribution is controlled by the Credit team.
     * - Traffic with upstream presentment, we will display the BNPL link.
     *   However, we have a kill switch/emegency button to turn off the
     *   experience.
     * - Traffic without upstream presentment, we will set up our ELMO to
     *   control it. If it returns treatment, we will display BNPL link. And we
     *   don't display the BNPL otherwise.
     */

    if (!hasTreatment('FEATURE_BNPL_KILL_SWITCH')) {
      return false;
    }

    const { checkoutSession } = getGlobals().initialGqlData;
    const hasUpstreamPresentment =
      checkoutSession.creditMeta?.upstreamCreditPresentment;

    if (hasUpstreamPresentment) {
      return true;
    }

    return hasTreatment('FEATURE_BNPL_WITHOUT_UPSTREAM_PRESENTMENT');
  },

  shouldShowSCAPhoneConfirmation: () =>
    hasTreatment('FEATURE_SIGNUP_MANDATORY_PHONE_CONFIRMATION'),

  shouldShowSplitLineOne: () => {
    // @TO_DO @TECH_DEBT we disable GAAC because we use ANS by zip code in BR
    // we should remove this one once we ramp up GAAC in BR
    return getLocale().country === 'BR';
  },

  shouldUseUlHeadings: () => hasTreatment('FEATURE_PAY_WITH_SECTION_HEADER'),

  threeDsV1Experience: () =>
    getFactors('FEATURE_3DS_V1_EXPERIENCE')?.experience,
};

const resolveFeatures = () => {
  const clientFeatures = Object.entries(resolvers).reduce(
    (accum, [featureName, resolver]) => ({
      ...accum,
      [featureName]: resolver(),
    }),
    {},
  );

  $logger.info('all_client_features_resolved', clientFeatures);

  return {
    ...clientFeatures,
    ...getInternalOverrides(),
  };
};

export { resolveFeatures };
